import {announce} from '@github-ui/aria-live'
import {Banner, Button, Dialog, FormControl, Link, SegmentedControl, TextInput} from '@primer/react'
import {useId, useMemo, useRef, useState} from 'react'

import {
  type McpServerConfig,
  type McpTransport,
  type McpValidationError,
  MCP_ALL_TOOLS,
  isRemoteTransport,
  pruneMcpServer,
  validateMcpServer,
} from '../types/mcp'
import {sendMcpUiEvent} from '../utils/mcp-analytics'
import {KeyValueFields, type KeyValuePair, pairsToRecord, recordToPairs, validatePairs} from './KeyValueFields'
import styles from './McpServerDialog.module.css'

export interface McpServerDialogProps {
  /** Name of the server being edited, or `undefined` when adding a new one. */
  editingName?: string
  /** Current definition when editing. */
  editingConfig?: McpServerConfig
  /** Names already in use, so a new server cannot silently replace one. */
  existingNames?: readonly string[]
  onClose: () => void
  onSave: (name: string, config: McpServerConfig) => void
  /** Disables the form and shows a spinner while the save is in flight. */
  busy?: boolean
  /** Reason the last save failed. */
  saveError?: string | null
  /** Disable for in-memory previews that do not use personal settings. */
  telemetryEnabled?: boolean
  returnFocusRef?: React.RefObject<HTMLElement | null>
  ref?: React.Ref<HTMLDivElement>
}

/**
 * The three transports offered in the UI.
 *
 * The schema also accepts `stdio`, which validates identically to `local`. There
 * is no reason to make a user choose between two spellings of one thing, so an
 * existing `stdio` server edits as Local and saves as `local`.
 */
const TRANSPORT_OPTIONS: ReadonlyArray<{value: McpTransport; label: string}> = [
  {value: 'local', label: 'Local'},
  {value: 'http', label: 'HTTP'},
  {value: 'sse', label: 'SSE'},
]

const NO_EXISTING_NAMES: readonly string[] = []

interface Draft {
  name: string
  type: McpTransport
  url: string
  command: string
  /** Space separated, or a JSON array when an argument needs one. */
  args: string
  timeout: string
  headers: KeyValuePair[]
  env: KeyValuePair[]
}

function toDraft(name: string | undefined, config: McpServerConfig | undefined): Draft {
  if (!config) {
    return {name: name ?? '', type: 'http', url: '', command: '', args: '', timeout: '', headers: [], env: []}
  }

  return {
    name: name ?? '',
    // `stdio` is shown and re-saved as `local`.
    type: config.type === 'stdio' ? 'local' : config.type,
    url: config.url ?? '',
    command: config.command ?? '',
    args: formatArgs(config.args ?? []),
    timeout: config.timeout?.toString() ?? '',
    headers: recordToPairs(config.headers),
    env: recordToPairs(config.env),
  }
}

/**
 * Reads the Arguments field.
 *
 * A leading `[` opts into JSON, which is the only way to express an argument
 * that contains whitespace or is empty. Anything else splits on whitespace.
 * Invalid JSON is reported on the Arguments field.
 */
function parseArgs(raw: string): string[] | undefined {
  const trimmed = raw.trim()
  if (trimmed.startsWith('[')) {
    try {
      const parsed: unknown = JSON.parse(trimmed)
      if (Array.isArray(parsed) && parsed.every(arg => typeof arg === 'string')) return parsed
    } catch {
      return undefined
    }
    return undefined
  }
  return trimmed.split(/\s+/).filter(Boolean)
}

/**
 * Writes the Arguments field.
 *
 * Uses the plain space-joined form when `parseArgs` reads it back unchanged,
 * and JSON otherwise, so an argument holding whitespace, an empty argument, or
 * one that itself looks like JSON survives an edit that never touched the field.
 */
function formatArgs(args: string[]): string {
  const joined = args.join(' ')
  const parsed = parseArgs(joined)
  const roundTrips = parsed?.length === args.length && parsed.every((arg, index) => arg === args[index])
  return roundTrips ? joined : JSON.stringify(args)
}

function toConfig(draft: Draft, existing?: McpServerConfig): McpServerConfig {
  const remote = isRemoteTransport(draft.type)
  const record = pairsToRecord(remote ? draft.headers : draft.env)

  return pruneMcpServer({
    ...existing,
    type: draft.type,
    tools: existing?.tools ?? [MCP_ALL_TOOLS],
    url: remote ? draft.url.trim() : undefined,
    headers: remote && record ? record : undefined,
    command: remote ? undefined : draft.command.trim(),
    args: remote ? undefined : parseArgs(draft.args),
    env: !remote && record ? record : undefined,
    timeout: draft.timeout.trim() ? Number(draft.timeout) : undefined,
  })
}

function errorFor(errors: McpValidationError[], path: string): string | undefined {
  return errors.find(error => error.path === path)?.message
}

/** Groups the per-argument errors, since each argument has no field of its own. */
function argumentErrors(errors: McpValidationError[]): string | undefined {
  return errors.find(error => error.path.startsWith('args'))?.message
}

/**
 * Adds or edits one personal MCP server.
 *
 * Validation runs on submit rather than on every keystroke, so a half-typed URL
 * is not reported as broken. Once a submit has failed, validation re-runs as the
 * user types, which lets errors clear as they are fixed.
 */
export function McpServerDialog({
  editingName,
  editingConfig,
  existingNames = NO_EXISTING_NAMES,
  onClose,
  onSave,
  busy = false,
  saveError = null,
  telemetryEnabled = true,
  returnFocusRef,
  ref: dialogRef,
}: McpServerDialogProps) {
  const isEditing = editingName !== undefined
  const [draft, setDraft] = useState<Draft>(() => toDraft(editingName, editingConfig))
  const [submitted, setSubmitted] = useState(false)
  const formId = useId()
  const formRef = useRef<HTMLFormElement>(null)

  const remote = isRemoteTransport(draft.type)
  const pairs = remote ? draft.headers : draft.env
  const pairErrors = validatePairs(pairs, remote)

  const nameRef = useRef<HTMLInputElement>(null)
  const urlRef = useRef<HTMLInputElement>(null)
  const commandRef = useRef<HTMLInputElement>(null)
  const argsRef = useRef<HTMLInputElement>(null)
  const timeoutRef = useRef<HTMLInputElement>(null)
  const pairErrorRef = useRef<HTMLInputElement>(null)

  const errors = useMemo(() => {
    // Validate the editable fields without imposing new rules on preserved options.
    const found = validateMcpServer(draft.name.trim(), toConfig(draft))
    if (!remote && parseArgs(draft.args) === undefined) {
      const argsError = found.find(error => error.path === 'args')
      if (argsError) argsError.message = 'Enter a JSON array containing only strings, such as ["-y", "my-server"].'
    }
    // Renaming onto another server would overwrite it, since the map is keyed by
    // name. Editing a server without renaming it is not a collision.
    const name = draft.name.trim()
    if (name && name !== editingName && existingNames.includes(name)) {
      found.push({path: 'name', message: 'A server with this name already exists.'})
    }
    return found
  }, [draft, editingName, existingNames, remote])

  const showErrors = submitted
  const update = (patch: Partial<Draft>) => {
    if (!busy) setDraft(current => ({...current, ...patch}))
  }

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault()
    if (busy) return
    setSubmitted(true)
    if (errors.length > 0 || Object.keys(pairErrors).length > 0) {
      if (telemetryEnabled) {
        sendMcpUiEvent('validation_failed', {
          source: 'dialog',
          action: isEditing ? 'edit' : 'create',
          transport: draft.type,
        })
      }
      focusFirstError()
      return
    }
    onSave(draft.name.trim(), toConfig(draft, editingConfig))
  }

  const handleClose = () => {
    if (busy) return
    if (telemetryEnabled) {
      sendMcpUiEvent('cancel', {source: 'dialog', action: isEditing ? 'edit' : 'create', transport: draft.type})
    }
    onClose()
  }

  // Focus stays on the submit button otherwise, so a screen reader never reaches
  // the message that explains why nothing happened.
  const focusFirstError = () => {
    const fields: Array<[boolean, React.RefObject<HTMLElement | null>]> = [
      [errorFor(errors, 'name') !== undefined, nameRef],
      [errorFor(errors, 'url') !== undefined, urlRef],
      [errorFor(errors, 'command') !== undefined, commandRef],
      [argumentErrors(errors) !== undefined, argsRef],
      [errorFor(errors, 'timeout') !== undefined, timeoutRef],
      [Object.keys(pairErrors).length > 0, pairErrorRef],
    ]
    const first = fields.find(([hasError, ref]) => hasError && ref.current)
    first?.[1].current?.focus()
    const errorCount = errors.length + Object.keys(pairErrors).length
    announce(
      errorCount === 1
        ? '1 problem with this MCP server. Review the highlighted field.'
        : `${errorCount} problems with this MCP server. Review the highlighted fields.`,
      {element: formRef.current ?? undefined},
    )
  }

  return (
    <Dialog
      ref={dialogRef}
      className={styles.dialog}
      onClose={handleClose}
      returnFocusRef={returnFocusRef}
      width="xlarge"
      title={isEditing ? `Edit ${editingName}` : 'Add MCP server'}
      subtitle="Manually added MCP servers do not support OAuth."
      renderFooter={() => (
        <Dialog.Footer>
          <Button onClick={handleClose} inactive={busy} aria-disabled={busy}>
            Cancel
          </Button>
          <Button
            type="submit"
            form={formId}
            variant="primary"
            loading={busy}
            loadingAnnouncement={isEditing ? 'Saving server' : 'Adding server'}
          >
            {isEditing ? 'Save' : 'Add server'}
          </Button>
        </Dialog.Footer>
      )}
    >
      {saveError ? (
        <Banner
          variant="critical"
          title="Server could not be saved"
          hideTitle
          description={saveError}
          className={styles.saveError}
        />
      ) : null}

      <form ref={formRef} id={formId} onSubmit={handleSubmit} className={styles.form} noValidate>
        <h2 className={styles.sectionHeading}>Configuration</h2>

        <div className={styles.identityRow}>
          <FormControl required className={styles.nameField}>
            <FormControl.Label requiredText="(required)">Server name</FormControl.Label>
            <TextInput
              ref={nameRef}
              readOnly={busy}
              value={draft.name}
              onChange={event => update({name: event.target.value})}
              placeholder="my-server"
              block
            />
            <FormControl.Caption>Letters, numbers, and the characters / _ -</FormControl.Caption>
            {showErrors && errorFor(errors, 'name') ? (
              <FormControl.Validation variant="error">{errorFor(errors, 'name')}</FormControl.Validation>
            ) : null}
          </FormControl>

          <FormControl className={styles.transportField}>
            <FormControl.Label>Server type</FormControl.Label>
            <SegmentedControl aria-label="Server type">
              {TRANSPORT_OPTIONS.map(option => (
                <SegmentedControl.Button
                  key={option.value}
                  selected={draft.type === option.value}
                  aria-disabled={busy}
                  onClick={() => update({type: option.value})}
                >
                  {option.label}
                </SegmentedControl.Button>
              ))}
            </SegmentedControl>
          </FormControl>
        </div>

        {remote ? (
          <FormControl required>
            <FormControl.Label requiredText="(required)">URL</FormControl.Label>
            <TextInput
              ref={urlRef}
              readOnly={busy}
              value={draft.url}
              onChange={event => update({url: event.target.value})}
              placeholder="https://api.example.com/mcp/"
              type="url"
              block
            />
            {showErrors && errorFor(errors, 'url') ? (
              <FormControl.Validation variant="error">{errorFor(errors, 'url')}</FormControl.Validation>
            ) : null}
          </FormControl>
        ) : (
          <>
            <FormControl required>
              <FormControl.Label requiredText="(required)">Command</FormControl.Label>
              <TextInput
                ref={commandRef}
                readOnly={busy}
                value={draft.command}
                onChange={event => update({command: event.target.value})}
                placeholder="npx"
                block
              />
              <FormControl.Caption>The program to run. Put its arguments in the field below.</FormControl.Caption>
              {showErrors && errorFor(errors, 'command') ? (
                <FormControl.Validation variant="error">{errorFor(errors, 'command')}</FormControl.Validation>
              ) : null}
            </FormControl>

            <FormControl>
              <FormControl.Label>Arguments</FormControl.Label>
              <TextInput
                ref={argsRef}
                readOnly={busy}
                value={draft.args}
                onChange={event => update({args: event.target.value})}
                placeholder="-y @modelcontextprotocol/server-filesystem /tmp"
                block
              />
              <FormControl.Caption>
                Separate arguments with spaces, or enter a JSON array if an argument contains spaces or is empty.
              </FormControl.Caption>
              {showErrors && argumentErrors(errors) ? (
                <FormControl.Validation variant="error">{argumentErrors(errors)}</FormControl.Validation>
              ) : null}
            </FormControl>
          </>
        )}

        <FormControl>
          <FormControl.Label>Timeout (milliseconds)</FormControl.Label>
          <TextInput
            ref={timeoutRef}
            readOnly={busy}
            value={draft.timeout}
            inputMode="numeric"
            onChange={event => update({timeout: event.target.value})}
            block
          />
          <FormControl.Caption>Optional. Enter a positive whole number of milliseconds.</FormControl.Caption>
          {showErrors && errorFor(errors, 'timeout') ? (
            <FormControl.Validation variant="error">{errorFor(errors, 'timeout')}</FormControl.Validation>
          ) : null}
        </FormControl>

        <KeyValueFields
          legend={remote ? 'Headers' : 'Environment variables'}
          caption={
            <>
              Create a secret in{' '}
              <Link inline href="/settings/copilot/personal-mcp/secrets">
                User secrets
              </Link>
              , then enter its name here - not its value.
            </>
          }
          keyLabel={remote ? 'Header name' : 'Variable name'}
          keyPlaceholder={remote ? 'Authorization' : 'API_TOKEN'}
          valuePlaceholder="MY_SECRET"
          addLabel={remote ? 'Add header' : 'Add variable'}
          pairs={pairs}
          busy={busy}
          errors={pairErrors}
          showErrors={showErrors}
          errorRef={pairErrorRef}
          onChange={next => update(remote ? {headers: next} : {env: next})}
        />
      </form>
    </Dialog>
  )
}
